import classNames from 'classnames';
import { ChangeEvent, ReactEventHandler, useCallback, useMemo, useRef } from 'react';
import InputMask, { Props as InputMaskProps } from 'react-input-mask';
import { Button } from 'yoso';
import { useTimer } from '~/components/Form/Number/Spin/SpinNumberInput.hooks';
import styles from './SpinNumberInput.module.scss';

export type SpinNumberInputProps = {
  className?: string;
} & InputMaskProps;

export const SpinNumberInput = ({
  className,
  name,
  value = 0,
  step = 1,
  min,
  max,
  autoComplete = 'off',
  onChange,
  ...props
}: SpinNumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const { start, stop } = useTimer();

  const onButtonDown: ReactEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const { name, disabled } = e.currentTarget;

      if (disabled) return;

      start((e) => {
        if (!inputRef.current) return;

        const valueAsNumber = Number(inputRef.current.value);
        const stepAsNumber = Number(step);
        const minAsNumber = Number(min) ?? -Infinity;
        const maxAsNumber = Number(max) ?? Infinity;
        const nextValueAsNumber =
          name === 'stepDown'
            ? Math.max(valueAsNumber - stepAsNumber, minAsNumber)
            : Math.min(valueAsNumber + stepAsNumber, maxAsNumber);

        if (valueAsNumber === nextValueAsNumber) {
          e.preventDefault();
          return;
        }

        inputRef.current.value = nextValueAsNumber.toString();

        onChange?.({ currentTarget: inputRef.current } as ChangeEvent<HTMLInputElement>);
      });
    },
    [max, min, onChange, start, step]
  );
  const onButtonUp = useCallback(() => {
    stop();
  }, [stop]);

  const isTouchDevice = 'ontouchstart' in window;
  const eventProps = useMemo(
    () =>
      isTouchDevice
        ? {
            onTouchStart: onButtonDown,
            onTouchEnd: onButtonUp,
          }
        : {
            onMouseDown: onButtonDown,
            onMouseUp: onButtonUp,
            onMouseLeave: onButtonUp,
          },
    [isTouchDevice, onButtonDown, onButtonUp]
  );

  return (
    <div className={classNames(styles.root, className)}>
      <InputMask
        className={styles.input}
        maskChar={null}
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        name={name}
        inputRef={(e) => {
          if (e) {
            inputRef.current = e;
          }
        }}
        autoComplete={autoComplete}
        onChange={onChange}
        data-event="click"
        data-event-target="input"
        {...props}
      />
      <Button
        className={classNames(styles.down, styles.button)}
        name="stepDown"
        disabled={min !== undefined && value <= min}
        data-event="click"
        data-event-target="step_down"
        {...eventProps}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="2"
          viewBox="0 0 12 2"
          className={styles.icon}>
          <path
            d="M0.84375 1.77475H5.21048H6.78952L11.1562 1.77475V0.225006L6.78952 0.225007H5.21048H0.84375V1.77475Z"
            fill="currentColor"
          />
        </svg>
      </Button>
      <Button
        className={styles.button}
        name="stepUp"
        disabled={max !== undefined && value >= max}
        data-event="click"
        data-event-target="step_up"
        {...eventProps}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={styles.icon}>
          <path
            d="M0.84375 6.77487H5.21048L5.21048 11.1562H6.78952L6.78952 6.77487H11.1562V5.22513H6.78952L6.78952 0.84375L5.21048 0.84375L5.21048 5.22513H0.84375V6.77487Z"
            fill="currentColor"
          />
        </svg>
      </Button>
    </div>
  );
};
